import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getMeetingsByClientId } from '../clients/meetingClient';
import useNavigation from '../common/navigation';
import { Client, Meeting } from '../common/types';
import { formatDateAsLongDate } from '../common/utils/dateUtils';
import { getInitials } from '../common/utils/utils';
import ErrorBanner from './ErrorBanner';

/**
 * Takes in a list of meetings and computes the most recent meeting
 * that occured in the past, and the closest meeting that will
 * occur in the future.
 */
const findLatestAndUpcomingMeetingDates = (
  meetings: Meeting[]
): {
  latestMeetingDate: string;
  upcomingMeetingDate: string;
} => {
  let latestMeetingDate = '';
  let upcomingMeetingDate = '';

  const now = new Date();

  for (const meeting of meetings) {
    if (meeting.dateTime < now) {
      latestMeetingDate = formatDateAsLongDate(meeting.dateTime); // This will be the last past meeting due to the sorted nature of the array
    } else if (meeting.dateTime > now && !upcomingMeetingDate) {
      upcomingMeetingDate = formatDateAsLongDate(meeting.dateTime);
      // Once the first future meeting is found, we break out of the loop since meetings are sorted.
      break;
    }
  }

  return { latestMeetingDate, upcomingMeetingDate };
};

const renderEditMenu = (
  clientId: number,
  handleDeletionRequest: () => void,
  handleEditRequest: () => void
) => {
  return (
    <Menu as="div" className="relative flex-none items-center">
      <MenuButton className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
        <span className="sr-only">Open options</span>
        <EllipsisVerticalIcon aria-hidden="true" className="h-5 w-5" />
      </MenuButton>
      <MenuItems
        transition
        className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <MenuItem>
          <button
            onClick={() => handleEditRequest()}
            className="inline-flex items-center block w-full px-2.5 p-1 text-left text-xs font-semibold leading-6 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
          >
            <PencilSquareIcon className="h-4 w-4 mr-2" />
            Edit
          </button>
        </MenuItem>
        <MenuItem>
          <button
            onClick={handleDeletionRequest}
            className="inline-flex items-center block w-full px-2.5 p-1 text-left text-xs font-semibold leading-6 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 disabled:text-gray-400 disabled:bg-white disabled:cursor-not-allowed"
          >
            <TrashIcon className="h-4 w-4 mr-2" />
            Delete<span className="sr-only">, {clientId}</span>
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
};

export default function ClientListItem({
  client,
  handleDeletionRequest,
  handleEditRequest
}: {
  client: Client;
  handleDeletionRequest: () => void;
  handleEditRequest: () => void;
}) {
  const [latestMeeting, setLatestMeeting] = useState('');
  const [upcomingMeeting, setUpcomingMeeting] = useState('');

  const { goToClientPage } = useNavigation();

  // Fetch meetings for client
  const { data: meetings, error } = useQuery({
    queryKey: ['getMeetingsByClientId', client.id],
    queryFn: () => getMeetingsByClientId(client.id)
  });

  useEffect(() => {
    if (meetings && meetings.length > 0) {
      const { latestMeetingDate, upcomingMeetingDate } =
        findLatestAndUpcomingMeetingDates(meetings);
      setLatestMeeting(latestMeetingDate);
      setUpcomingMeeting(upcomingMeetingDate);
    }
  }, [meetings]);

  return (
    <li className="py-6 bg-white">
      <div className="flex space-x-4 sm:space-x-6">
        <div className="flex lg:items-center">
          <div className="relative inline-flex items-center justify-center w-12 h-12 overflow-hidden bg-indigo-50 rounded-full truncate">
            <span className="font-medium text-indigo-800">{getInitials(client.name)}</span>
          </div>
        </div>
        <div className="w-full flex-1 flex-col sm:flex-row flex sm:space-x-6">
          <div className="min-w-0 flex-auto">
            <button
              onClick={() => goToClientPage(client.id)}
              className="max-w-full text-lg text-left text-wrap break-words font-semibold leading-6 text-gray-900 hover:text-indigo-700 transition-color duration-300"
            >
              {client.name}
            </button>

            <div className="mt-1 flex items-center gap-x-2 text-sm leading-5 text-gray-500">
              <p className="whitespace-nowrap">{meetings?.length} Meetings</p>

              {!latestMeeting && !upcomingMeeting && (
                <span className="flex items-center">
                  <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 mr-2 fill-current">
                    <circle cx={1} cy={1} r={1} />
                  </svg>
                  <p className="truncate">No past or scheduled meetings</p>
                </span>
              )}
              {latestMeeting && (
                <span className="flex items-center">
                  <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 mr-2 fill-current">
                    <circle cx={1} cy={1} r={1} />
                  </svg>
                  <p className="truncate">Last meeting on {latestMeeting}</p>
                </span>
              )}
              {upcomingMeeting && (
                <span className="flex items-center">
                  <svg viewBox="0 0 2 2" className="h-0.5 w-0.5 mr-2 fill-current">
                    <circle cx={1} cy={1} r={1} />
                  </svg>
                  <p className="truncate">Upcoming meeting on {upcomingMeeting}</p>
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-none items-center gap-x-4 mt-3 md:mt-0 md:ml-auto">
            <button
              onClick={() => goToClientPage(client.id)}
              className="inline-flex gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none"
              // className="rounded-md bg-indigo-600 px-3.5 py-2 text-xs font-bold text-white shadow-sm ring-1 ring-inset hover:bg-indigo-500 transition ease-in-out duration-150 ring-gray-300 hover:bg-gray-50 sm:block "
            >
              View details
            </button>
            {renderEditMenu(client.id, handleDeletionRequest, handleEditRequest)}
          </div>
        </div>
      </div>
      <div className="w-full">
        {/* Error loading client meetings */}
        {error && <ErrorBanner message={error.message} />}
      </div>
    </li>
  );
}
